import { CSSProperties } from 'react'
import { Flex, Button, Popconfirm, Tooltip } from 'antd'
import { EditOutlined } from '@ant-design/icons'
import { path } from 'src/libs'
import {
  closeModalRequestView,
  openModalRequestsApprove,
  openModalRequestsDelete,
  openModalRequestsReject,
  openModalRequestsToSubmit,
  submitRequestEarlyPayment,
  submitRequestsReturnToDraft,
} from 'src/pages/budget/budgetRequests/model'
import { TStatus, TStatusPeriod } from 'src/pages/budget/types'
import { BudgetRequest } from 'src/pages/budget/budgetRequests/types'
import { useNavigate } from 'react-router-dom'
import { STATUS_PERIOD } from 'src/pages/budget/const'
import { useCeoTeam } from 'src/pages/budget/hook'
import { useUserProfile } from 'src/features/user'
import { formatAmount } from 'src/pages/budget/budgetRequests/currency'

type Props = {
  requestStatus: TStatus
  isOwn: boolean
  request: BudgetRequest
  periodStatus?: TStatusPeriod
}

type ButtonProps = {
  request: BudgetRequest
  style?: CSSProperties
}

const EditButton = ({ request, style }: ButtonProps) => {
  const navigate = useNavigate()

  const goToEdit = () => {
    navigate(path.budget.budgets.edit(String(request.id)))
    closeModalRequestView()
  }

  return (
    <Button
      size="large"
      color="geekblue"
      variant="outlined"
      style={style}
      icon={<EditOutlined />}
      onClick={goToEdit}
    >
      Редактировать
    </Button>
  )
}

const ApproveButton = ({ request, style }: ButtonProps) => (
  <Button
    type="primary"
    size="large"
    variant="solid"
    color="green"
    style={style}
    onClick={() => {
      openModalRequestsApprove({
        items: [
          {
            id: request.id,
            description: request.description,
            availableApprovalSteps: request.availableApprovalSteps,
          },
        ],
      })
      closeModalRequestView()
    }}
  >
    Согласовать
  </Button>
)

const RejectButton = ({ request, style }: ButtonProps) => (
  <Button
    size="large"
    variant="solid"
    color="volcano"
    style={style}
    onClick={() => {
      openModalRequestsReject({
        items: [
          {
            id: request.id,
            description: request.description,
            availableApprovalSteps: request.availableApprovalSteps,
          },
        ],
      })
      closeModalRequestView()
    }}
  >
    Отклонить
  </Button>
)

const ReturnToDraftButton = ({ request, style }: ButtonProps) => (
  <Button
    size="large"
    variant="solid"
    style={style}
    onClick={() => {
      submitRequestsReturnToDraft({ items: [request] })
      closeModalRequestView()
    }}
  >
    Вернуть в черновик
  </Button>
)

const RequestEarlyPaymentButton = ({ request, style }: ButtonProps) => (
  <Popconfirm
    title="Запросить досрочную оплату?"
    description="Заявка будет помечена как срочная."
    okText="Подтвердить"
    cancelText="Отмена"
    onConfirm={() => {
      submitRequestEarlyPayment({ id: request.id })
      closeModalRequestView()
    }}
  >
    <Button
    size="large"
    color="orange"
    variant="solid"
    style={style}
    >
      Запросить досрочную оплату
    </Button>
  </Popconfirm>
)

const DeleteButton = ({ request, style }: ButtonProps) => (
  <Button
    color="volcano"
    size="large"
    variant="solid"
    style={style}
    onClick={() => {
      openModalRequestsDelete({ items: [request] })
      closeModalRequestView()
    }}
  >
    Удалить
  </Button>
)

const SubmitButton = ({ request, style }: ButtonProps) => (
  <Button
    color="cyan"
    size="large"
    variant="solid"
    style={style}
    onClick={() => {
      openModalRequestsToSubmit({ items: [request] })
      closeModalRequestView()
    }}
  >
    Отправить на согласование
  </Button>
)

const PaymentButton = ({
  request,
  style,
  isPaymentAvailable,
}: ButtonProps & { isPaymentAvailable: boolean }) => {
  const navigate = useNavigate()

  //todo
  return null

  return (
    <Tooltip
      title={
        isPaymentAvailable
          ? null
          : 'Оплата будет доступна после закрытия подачи заявок'
      }
    >
      <Button
        type={isPaymentAvailable ? 'primary' : 'default'}
        size="large"
        style={style}
        disabled={!isPaymentAvailable}
        variant={isPaymentAvailable ? 'outlined' : undefined}
        color={isPaymentAvailable ? 'green' : undefined}
        onClick={() => {
          navigate(path.budget.budgets.createPaymentRequest(String(request.id)))
        }}
      >
        Создать заявку на оплату {formatAmount(request.amount, request.currency)}{' '}
        {request.currency}
      </Button>
    </Tooltip>
  )
}

export const ButtonContentByStatus = ({
  requestStatus,
  request,
  isOwn,
  periodStatus,
}: Props) => {
  const { ceoTeam } = useCeoTeam()
  const userProfile = useUserProfile()
  const isCeo = ceoTeam.includes(userProfile.id)
  const isPaymentAvailable =
    periodStatus === STATUS_PERIOD.OPEN_PAYMENT ||
    (request.isEarlyPayment && periodStatus !== STATUS_PERIOD.CLOSED)
  const canRequestEarlyPayment =
    !request.isEarlyPayment &&
    ((isOwn && ['draft', 'submitted', 'in_review'].includes(requestStatus)) ||
      (isCeo && requestStatus === 'approved'))

  const content: Record<TStatus, JSX.Element> = {
    draft: isOwn ? (
      <Flex vertical gap={16}>
        <EditButton request={request} />

        <Flex gap={16}>
          <DeleteButton style={{ flex: 1 }} request={request} />
          <SubmitButton style={{ flex: 1 }} request={request} />
        </Flex>
      </Flex>
    ) : (
      <></>
    ),

    submitted: isOwn ? (
      <Flex vertical gap={16}>
        <EditButton request={request} />
        <Flex gap={16}>
          <ReturnToDraftButton request={request} style={{ flex: 1 }} />
          {canRequestEarlyPayment && (
            <RequestEarlyPaymentButton request={request} style={{ flex: 1 }} />
          )}
        </Flex>
      </Flex>
    ) : (
      <Flex vertical gap={16}>
        <Flex gap={16}>
          <ApproveButton style={{ flex: 1 }} request={request} />
        </Flex>

        <Flex gap={16}>
          <EditButton style={{ flex: 1 }} request={request} />
          <RejectButton style={{ flex: 1 }} request={request} />
        </Flex>
      </Flex>
    ),

    in_review:
      isOwn || request.availableApprovalSteps.length === 0 ? (
        <></>
      ) : (
        <Flex vertical gap={16}>
          <Flex gap={16}>
            <ApproveButton style={{ flex: 1 }} request={request} />
          </Flex>

          <Flex gap={16}>
            <EditButton style={{ flex: 1 }} request={request} />
            <RejectButton style={{ flex: 1 }} request={request} />
          </Flex>
        </Flex>
      ),

    approved: (
      <PaymentButton
        request={request}
        isPaymentAvailable={isPaymentAvailable}
      />
    ),

    rejected: isOwn ? (
      <Flex vertical gap={16}>
        <EditButton request={request} />
        <ReturnToDraftButton request={request} style={{ width: '100%' }} />
      </Flex>
    ) : (
      <></>
    ),

    pending_payment: <></>,
    paid: <></>,
    partial_payment: <></>,
    expired: <></>,
  }

  return (
    <Flex vertical gap={16}>
      {content[requestStatus]}
      {canRequestEarlyPayment && requestStatus !== 'submitted' && (
        <RequestEarlyPaymentButton
          request={request}
          style={{ width: '100%' }}
        />
      )}
    </Flex>
  )
}
